'use client';
import { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import Image from '@repo/ui/src/components/Image';
import Section from '@repo/ui/src/components/Section';
import { useReducedMotion } from '@repo/ui/src/utils/useMediaQuery';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { imageFullWidthSmAndUp } from '@style/variables.js';
import { Banner, PelletImage, Heading, HeadingLight, ToutContent } from './styles';
import { MediaType } from '@repo/ui/src/types/image';
import { MetaProps } from '@repo/ui/src/types/meta';
import { ToutContentProps } from '@repo/ui/src/types/toutContent';

interface PelletImageProps {
  endpoint: string;
  name: string;
}

export interface Props {
  toutContent: ToutContentProps;
  heroImage: {
    image: MediaType;
    static: boolean;
    _meta: MetaProps;
  };
  _testID?: string;
}

const FuelHero = ({ toutContent, heroImage, _testID }: Props) => {
  const ref = useRef<HTMLElement>(null);
  const reduceMotion = useReducedMotion();

  const heroImageUrl = buildImageSrc(heroImage, imageFullWidthSmAndUp, 10, 4);
  const pelletImage: PelletImageProps = {
    endpoint: 'traeger',
    name: 'pellets-warm',
  };
  const pelletImageUrl = buildImageSrc(pelletImage, 'auto', 0, 0);

  useEffect(() => {
    if (reduceMotion) return;
    const element = ref.current;
    if (element) {
      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: element,
          start: 'top bottom',
          end: 'bottom 25%',
          scrub: 1,
          // markers: true,
        },
      });
      timeline.to(element.querySelector('.pellet-image'), {
        yPercent: -35,
      });
    }
  }, [reduceMotion]);

  return (
    <Section
      as="section"
      texture
      rootRef={ref}
      data-test-id={`fuel_hero_${_testID}`}
      className="tw-pb-12 sm:tw-pt-20 sm:tw-pb-15 md:tw-pt-[30vh] md:tw-pb-20"
      zIndexNone
    >
      <PelletImage className="pellet-image">
        <Image src={pelletImageUrl} layout="fill" alt="" role="presentation" />
      </PelletImage>
      <Banner {...{ heroImageUrl }}>
        {(toutContent.headingLight || toutContent.heading) && (
          <Heading
            align="center"
            variant="h1"
            component={toutContent.headerVariation?.headerVariation || 'h2'}
            className="flexible"
          >
            {toutContent.headingLight && (
              <>
                <HeadingLight>{toutContent.headingLight}</HeadingLight>
                {/* Add spaces for SEO */}
                <span className="sr-only">&nbsp;</span>
              </>
            )}
            {toutContent.heading}
          </Heading>
        )}
      </Banner>
      <ToutContent
        align="center"
        fullWidth
        description={toutContent.description}
        buttons={toutContent.buttons}
      />
    </Section>
  );
};

export default FuelHero;
